import { Maybe } from '@crocodille/common';
import { RefObject, useEffect } from 'react';
import { useRef } from 'react';

import { useThrottledCallback } from './use-throttled-callback';
import { useEventListener } from './use-event-listener';
import { useIsomorphicLayoutEffect as useLayoutEffect } from './use-isomorphic-layout-effect';

/**
 * Uses the scroll position of the window.
 *
 * The provided callback will be called once per animation frame.
 *
 * @param callback The callback to call when scroll position updates
 */
export function useScroll(
  callback: (scrollY: number) => void,
  targetRef:
    | RefObject<HTMLElement | null>
    | Maybe<Window | Document> = typeof window !== 'undefined'
    ? document
    : null,
) {
  const callbackRef = useRef(callback);
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  //
  // Scroll events are fired a lot, so the callback should be invoked
  // only once per animation frame, with the latest position.
  //
  const throttled = useThrottledCallback(() => {
    callbackRef.current(window.scrollY ?? window.pageYOffset);
  }, 'animationFrame');

  //
  // We call the callback immediately so the current position is reflected.
  //
  useLayoutEffect(() => {
    callbackRef.current(window.scrollY ?? window.pageYOffset);
  }, []);

  useEventListener(
    'scroll',
    throttled,
    targetRef as any,
    //
    // The passive option will tell the browser that listener will not prevent
    // the default behavior of the event and is able to ignore the result of
    // listener if it takes too long to process.
    //
    { passive: true },
  );
}
